import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { apiPost, ApiError } from "../api/client";
import { GlobeLocaleDropdown } from "../components/GlobeLocaleDropdown";
import { Banner } from "../components/Banner";
import { Button } from "../components/Button";
import { TextField } from "../components/TextField";

type Step = "step1" | "step2" | "step3";

/**
 * PM 3-step forgot-password flow (PMAUTH-8.4).
 * Step 1: username → security_question_code
 * Step 2: question + answer → reset_token
 * Step 3: reset_token + new_password (+ confirm) → OK_PM_FORGOT_RESET → /login
 */
export function ForgotPassword() {
  const intl = useIntl();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("step1");
  const [username, setUsername] = useState("");
  const [securityQuestionCode, setSecurityQuestionCode] = useState("");
  const [answer, setAnswer] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleStep1 = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await apiPost<{ security_question_code: string }>(
        "/api/auth/pm/forgot-password",
        { username },
      );
      setSecurityQuestionCode(res.security_question_code);
      setStep("step2");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(intl.formatMessage({ id: err.code, defaultMessage: err.code }));
      } else {
        setError(intl.formatMessage({ id: "ERR_GENERIC" }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await apiPost<{ reset_token: string }>(
        "/api/auth/pm/forgot-password",
        { username, answer },
      );
      setResetToken(res.reset_token);
      setStep("step3");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(intl.formatMessage({ id: err.code, defaultMessage: err.code }));
      } else {
        setError(intl.formatMessage({ id: "ERR_GENERIC" }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 7) {
      setError(intl.formatMessage({ id: "ERR_PASSWORD_TOO_SHORT" }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(intl.formatMessage({ id: "ERR_PASSWORD_MISMATCH" }));
      return;
    }

    setLoading(true);
    try {
      await apiPost("/api/auth/pm/forgot-password", {
        reset_token: resetToken,
        new_password: newPassword,
      });
      navigate("/login", {
        replace: true,
        state: { banner: intl.formatMessage({ id: "OK_PM_FORGOT_RESET" }) },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(intl.formatMessage({ id: err.code, defaultMessage: err.code }));
      } else {
        setError(intl.formatMessage({ id: "ERR_GENERIC" }));
      }
    } finally {
      setLoading(false);
    }
  };

  const headingId =
    step === "step1"
      ? "LABEL_RECOVER_PASSWORD"
      : step === "step2"
        ? "LABEL_RECOVER_PASSWORD"
        : "LABEL_SET_NEW_PASSWORD";

  return (
    <div
      className="min-h-screen relative bg-primary"
      data-testid="forgot-password-page"
    >
      {/* Brand chrome shared with Login (2026-05-17 FIND-A): 3-layer wash + logo block + centered card group.
          Same layering rationale as Login.tsx — see that file for the V-M9 cold-paint commentary. */}
      <div
        className="absolute inset-0 bg-[url('/brand/watercolor-master.jpg')] bg-cover bg-center"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-primary/55" aria-hidden="true" />

      <div className="relative min-h-screen flex flex-col items-center pbs-6">
        <div className="flex flex-col items-center mbe-4">
          <img
            src="/brand/unayzah-municipality-logo.png"
            alt=""
            aria-hidden="true"
            className="w-24 sm:w-28 h-auto mbe-1"
            data-testid="forgot-main-logo"
          />
          <p
            className="text-[10px] font-medium text-white text-center leading-tight"
            data-testid="forgot-org-parent"
          >
            <FormattedMessage id="app_org_parent_dept" />
          </p>
          <p
            className="text-[10px] font-medium text-white text-center leading-tight"
            data-testid="forgot-org-sub"
          >
            <FormattedMessage id="app_org_sub_dept" />
          </p>
        </div>

        <div className="w-[400px] flex-1 flex flex-col justify-center pbe-8">
          <h2
            className="ltr:text-base rtl:text-lg font-semibold text-white text-center mbe-3"
            data-testid="forgot-app-name-full"
          >
            <FormattedMessage id="app_name_full" />
          </h2>

          <div className="bg-surface-raised rounded-lg shadow-sm border border-divider p-8">
            <div className="flex items-center justify-between mbe-6">
              <h1 className="text-2xl font-semibold text-on-surface">
                <FormattedMessage id={headingId} />
              </h1>
              <GlobeLocaleDropdown />
            </div>

          {error && (
            <div className="mbe-4">
              <Banner variant="error" data-testid="forgot-error-banner">
                {error}
              </Banner>
            </div>
          )}

          {step === "step1" && (
            <form onSubmit={handleStep1} className="flex flex-col gap-4">
              <TextField
                id="fp-username"
                label={intl.formatMessage({ id: "LABEL_USERNAME" })}
                type="text"
                value={username}
                onChange={(v) => { setUsername(v); setError(null); }}
                autoComplete="username"
                data-testid="fp-username-input"
              />
              <Button type="submit" fullWidth disabled={loading} data-testid="find-question-button">
                <FormattedMessage id="LABEL_FIND_MY_QUESTION" />
              </Button>
              <Link
                to="/login"
                className="text-sm text-primary hover:text-primary-tint underline-offset-4 hover:underline text-center"
              >
                <FormattedMessage id="LABEL_SIGN_IN" />
              </Link>
            </form>
          )}

          {step === "step2" && (
            <form onSubmit={handleStep2} className="flex flex-col gap-4" data-testid="step2-form">
              <p className="text-base text-on-surface font-semibold">
                <FormattedMessage
                  id={securityQuestionCode}
                  defaultMessage={securityQuestionCode}
                />
              </p>
              <TextField
                id="fp-answer"
                label={intl.formatMessage({ id: "LABEL_SECURITY_ANSWER" })}
                type="text"
                value={answer}
                onChange={(v) => { setAnswer(v); setError(null); }}
                data-testid="fp-answer-input"
              />
              <Button type="submit" fullWidth disabled={loading} data-testid="verify-answer-button">
                <FormattedMessage id="LABEL_VERIFY_ANSWER" />
              </Button>
            </form>
          )}

          {step === "step3" && (
            <form onSubmit={handleStep3} className="flex flex-col gap-4" data-testid="step3-form">
              <TextField
                id="fp-new-password"
                label={intl.formatMessage({ id: "LABEL_NEW_PASSWORD" })}
                type="password"
                value={newPassword}
                onChange={(v) => { setNewPassword(v); setError(null); }}
                autoComplete="new-password"
                data-testid="fp-new-password-input"
              />
              <TextField
                id="fp-confirm-password"
                label={intl.formatMessage({ id: "LABEL_CONFIRM_PASSWORD" })}
                type="password"
                value={confirmPassword}
                onChange={(v) => { setConfirmPassword(v); setError(null); }}
                autoComplete="new-password"
                data-testid="fp-confirm-password-input"
              />
              <Button type="submit" fullWidth disabled={loading} data-testid="save-new-password-button">
                <FormattedMessage id="LABEL_SAVE_NEW_PASSWORD" />
              </Button>
            </form>
          )}
          </div>

          <p
            className="text-xs text-white/70 text-center mbs-4"
            data-testid="forgot-app-publisher"
          >
            <FormattedMessage id="app_publisher" />
          </p>
        </div>
      </div>
    </div>
  );
}
